import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, getDoc, query, where, serverTimestamp, runTransaction, onSnapshot } from "firebase/firestore";
import { db, auth, storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { formatCurrencyUY, formatCurrencyUSD, formatDateLocalized } from "../utils/formatters";
import { useTranslation } from "react-i18next";
import { modalBoxWide } from "../utils/modalLayout";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity, useIsReadOnly } from "../context/EffectiveIdentityContext";
import { useToast } from "../components/Toast";
import { tolerantId } from "../utils/firestoreHelpers";
import { leerPagosParaResidente } from "../utils/consultasEdificio";
import { leerFichaResidente } from "../utils/fichaResidente";
import { datosPagoInformado, excedeLoAdeudado, saldoPendienteDePago } from "../utils/pagoInformado";
import { HABI_API_BASE_URL, getAuthHeaders } from "../utils/habiApi";
import { initiateDlocalPayment } from "../utils/dlocalApi";
import { FaFileInvoiceDollar, FaClock, FaCheckCircle, FaEye, FaCreditCard, FaCalendarAlt, FaHistory, FaExternalLinkAlt, FaSwimmingPool, FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import {
  filterPaymentsVisibleToResidents,
  isExpenseApartmentPublishedToResidents,
  isPaymentPublishedToResidents,
} from "../utils/cierrePublicacion";
import { friendlyBreakdownName } from "../utils/fundHelpers";
import { esPagoDeAlquiler } from "../utils/unidadesEdificio";
import { nombrePeriodo } from "../utils/alquileresEdificio";
import { detalleCorto } from "../utils/detalleGasto";
import { useSelectedPayments } from "../services/paymentCart";
import PaymentCheckoutModal from "../components/PaymentCheckoutModal";
import { useIsMobile } from "../hooks/useResponsive";
import { AppScreen, AppHeader, AppCard, AppButton, Badge, AppEmptyState, SectionLabel, Money } from "../components/app";
import { appColors, appType, appSpacing, appRadius, appShadows } from "../utils/appTheme";

// Input full-width para los DatePicker de la fila de filtros mobile. La clase
// "habitia-datepicker" no está definida en ningún CSS y el wrapper de
// react-datepicker es inline-block, por lo que no respeta el flex:1 del padre.
// Con un customInput controlamos el ancho (100%) y el estilo con tokens del theme.
const MobileDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    value={value}
    onClick={onClick}
    onChange={() => {}}
    placeholder={placeholder}
    readOnly
    style={{
      width: "100%",
      boxSizing: "border-box",
      padding: "10px 12px",
      border: `1px solid ${appColors.line}`,
      borderRadius: appRadius.button,
      background: appColors.surface,
      fontFamily: appType.fontFamily,
      fontSize: 13.5,
      fontWeight: 400,
      color: appColors.ink,
      cursor: "pointer",
    }}
  />
));

const Expensas = ({ setSelected, goBack }) => {
  const { t } = useTranslation("expensas");
  const isMobile = useIsMobile();
  const { selectedBuilding, selectedUnit, buildings, soloAlquilerEdificio } = useBuilding();
  const { email: effEmail, userid: effUserid } = useEffectiveIdentity();
  const readOnly = useIsReadOnly();
  const { showToast } = useToast();
  const [showCartCheckout, setShowCartCheckout] = useState(false);
  // Bump para forzar reload de payments después de un pago vía carrito.
  // `fetchPayments` vive dentro del useEffect — usamos un dep extra para
  // re-disparar el fetch sin mover la función al scope del componente.
  const [reloadKey, setReloadKey] = useState(0);

  const [payments, setPayments] = useState([]);
  // Payments de inquilinos de unidades donde el residente actual es owner
  // (toggle "Incluir pagos de mis inquilinos"). Off por default.
  const [tenantPayments, setTenantPayments] = useState([]);
  const [includeTenantPayments, setIncludeTenantPayments] = useState(false);
  const [pagoSeleccionado, setPagoSeleccionado] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detallePago, setDetallePago] = useState(null);
  const [gastosDetalle, setGastosDetalle] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [desglosePayments, setDesglosePayments] = useState({}); // { paymentId: { ownerAmount, tenantAmount } }
  const [desgloseGastos, setDesgloseGastos] = useState({}); // { expenseId: { ownerAmount, tenantAmount } }
  const [userApartments, setUserApartments] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [suppliersMap, setSuppliersMap] = useState({});
  const [amenityChargesDetalle, setAmenityChargesDetalle] = useState([]);

  // E1 — "Explicame este gasto común"
  const [explainText, setExplainText] = useState(null);
  const [explainLoading, setExplainLoading] = useState(false);

  const handleExplainExpense = async () => {
    if (!detallePago?.id || !selectedBuilding?.buildingid) return;
    try {
      setExplainLoading(true);
      setExplainText(null);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/explain-expense`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          payment_id: detallePago.id,
          building_id: String(selectedBuilding.buildingid),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || t("toast.explainFailed"), "error");
        return;
      }
      const data = await resp.json();
      setExplainText(data.text || "");
    } catch (e) {
      console.error("explain-expense error:", e);
      showToast(e.message || t("toast.unexpectedError"), "error");
    } finally {
      setExplainLoading(false);
    }
  };

  // Helper para normalizar Timestamp/Date/string a Date
  const toDate = (maybeTs) => {
    if (!maybeTs) return null;
    if (maybeTs.seconds) return new Date(maybeTs.seconds * 1000);
    // Firestore también puede devolverte Date nativa si se guardó así
    if (maybeTs instanceof Date) return maybeTs;
    return new Date(maybeTs);
  };

  useEffect(() => {
  const fetchPayments = async () => {
    try {
      if (!selectedBuilding?.buildingid) return;

      // Usuario actual (identidad efectiva: soporta "ver como residente")
      if (!effEmail) return;

      // Resolver userId desde 'users' (fallback al uid si no existe el doc). En
      // "ver como" viene del contexto: quien mira es staff y con C10 no lista
      // users por email.
      const ficha = await leerFichaResidente(db, { email: effEmail, userid: effUserid, viendoComo: readOnly });
      const userId = ficha ? ficha.userid : auth.currentUser?.uid;
      
      // Normalizar userId a string para que coincida con cómo se guarda
      const normalizedUserId = String(userId);
      setCurrentUserId(normalizedUserId);
      

      // Obtener todas las unidades donde el usuario es owner o tenant
      const apartmentsQuery = query(
        collection(db, "apartments"),
        where("building", "in", tolerantId(selectedBuilding.buildingid))
      );
      const apartmentsSnap = await getDocs(apartmentsQuery);
      const apartments = apartmentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Filtrar unidades donde el usuario es owner o tenant
      const userApts = apartments.filter(
        (apt) =>
          String(apt.owner) === normalizedUserId ||
          String(apt.tenant) === normalizedUserId ||
          String(apt.ownerSecondary) === normalizedUserId ||
          String(apt.tenantSecondary) === normalizedUserId
      );
      setUserApartments(userApts);

      const visiblePaymentUserIds = new Set([normalizedUserId]);
      userApts.forEach((apt) => {
        if (String(apt.ownerSecondary) === normalizedUserId && apt.owner != null && String(apt.owner).trim() !== "") {
          visiblePaymentUserIds.add(String(apt.owner));
        }
        if (String(apt.tenantSecondary) === normalizedUserId && apt.tenant != null && String(apt.tenant).trim() !== "") {
          visiblePaymentUserIds.add(String(apt.tenant));
        }
      });

      // Traer pagos visibles para el usuario (propio + principal si es secundario).
      // El ex-ocupante solo lee los suyos (reglas 0D): si la consulta por
      // edificio se niega, leerPagosParaResidente la repite con sus userid. En
      // "ver como" no hay segundo intento (el que mira es staff del edificio).
      const allBuildingPayments = await leerPagosParaResidente(db, selectedBuilding.buildingid, {
        uid: readOnly ? null : auth.currentUser?.uid || null,
      });
      const ownPayments = allBuildingPayments.filter(
        (p) => String(p.userid) === normalizedUserId
      );
      const sourcePayments =
        ownPayments.length > 0
          ? ownPayments
          : allBuildingPayments.filter((p) => visiblePaymentUserIds.has(String(p.userid)));
      const data = filterPaymentsVisibleToResidents(sourcePayments);
      setPayments(data);

      // FIN-027: Notificar pagos rechazados con motivo al cargar la pantalla
      const rechazados = data.filter(p => p.status === "rechazado" && p.rejectionReason);
      if (rechazados.length > 0) {
        showToast(t("toast.rejectedPayments", { count: rechazados.length }), "warning");
      }

      // Cargar payments de inquilinos de unidades donde soy owner (toggle).
      // Solo del edificio actual; mismo patrón que en MisPagos.
      const myOwnerApartmentIds = new Set(
        userApts
          .filter(
            (apt) =>
              String(apt.owner) === normalizedUserId ||
              String(apt.ownerSecondary) === normalizedUserId
          )
          .map((apt) => apt.id)
      );
      const tenantPays = allBuildingPayments.filter((p) => {
        if (String(p.userid) === normalizedUserId) return false;
        if (!p.apartmentId) return false;
        return myOwnerApartmentIds.has(p.apartmentId);
      });
      // Sin el nombre del inquilino: un residente no lee fichas ajenas de
      // users (C10). La fila dice "tu inquilino" y la unidad.
      setTenantPayments(filterPaymentsVisibleToResidents(tenantPays));

      // Cargar mapa de proveedores. Best-effort: la demo no lee el catálogo
      // global (reglas 0D); sin nombres, el desglose sigue cargando abajo.
      const sMap = {};
      try {
        const suppliersSnap = await getDocs(collection(db, "supplier"));
        suppliersSnap.docs.forEach((d) => {
          const sData = d.data();
          sMap[sData.supplierId] = sData.name;
        });
      } catch (supErr) {
        console.warn("No se pudieron cargar los proveedores:", supErr?.code || supErr?.message);
      }
      setSuppliersMap(sMap);

      // Cargar desglose para pagos de cierre de mes (filtrar por unidad si hay selección)
      const aptsForDesglose = selectedUnit ? [selectedUnit] : userApts;
      await cargarDesglosePayments(data, normalizedUserId, aptsForDesglose);
    } catch (error) {
      console.error("Error al traer pagos:", error);
    }
  };

  const cargarDesglosePayments = async (paymentsData, userId, userApartmentsOverride) => {
    try {
      if (!selectedBuilding?.buildingid) {
        setDesglosePayments({});
        return;
      }
      
      const desglose = {};
      
      // Filtrar solo pagos de cierre de mes publicados (tienen month y year). El
      // alquiler de una unidad del edificio no tiene desglose de gastos comunes.
      const pagosCierre = paymentsData.filter(
        (p) => p.month && p.year && isPaymentPublishedToResidents(p) && !esPagoDeAlquiler(p)
      );
      
      if (pagosCierre.length === 0) {
        setDesglosePayments({});
        return;
      }
      
      let userApartments = userApartmentsOverride;
      if (!userApartments?.length) {
        const apartmentsQuery = query(
          collection(db, "apartments"),
          where("building", "in", tolerantId(selectedBuilding.buildingid))
        );
        const apartmentsSnap = await getDocs(apartmentsQuery);
        const apartments = apartmentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        userApartments = apartments.filter(
          (apt) =>
            String(apt.owner) === String(userId) ||
            String(apt.tenant) === String(userId) ||
            String(apt.ownerSecondary) === String(userId) ||
            String(apt.tenantSecondary) === String(userId)
        );
      }
      
      if (userApartments.length === 0) {
        setDesglosePayments({});
        return;
      }
      
      const apartmentIds = userApartments.map(apt => apt.id);
      
      // Para cada pago de cierre, buscar el desglose
      for (const pago of pagosCierre) {
        const expensesApartmentsQuery = query(
          collection(db, "expensesapartments"),
          where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
          where("month", "==", pago.month),
          where("year", "==", pago.year)
        );
        const expensesApartmentsSnap = await getDocs(expensesApartmentsQuery);
        const expensesApartments = expensesApartmentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Fix multi-unidad: si el payment tiene apartmentId, el desglose
        // debe calcularse SOLO con esa unidad. Si no (legacy), iteramos
        // sobre todas las unidades del residente.
        const aptIdsParaEstePayment = pago.apartmentId
          ? [pago.apartmentId]
          : apartmentIds;

        // Filtrar expensesapartments por la(s) unidad(es) relevantes + publicados
        const userExpensesApartments = expensesApartments.filter(
          (ea) =>
            aptIdsParaEstePayment.includes(ea.apartment) &&
            isExpenseApartmentPublishedToResidents(ea)
        );

        // Sumar montos owner y tenant SOLO con las unidades relevantes
        let ownerAmount = 0;
        let tenantAmount = 0;

        for (const ea of userExpensesApartments) {
          const apt = userApartments.find(a => a.id === ea.apartment);
          if (!apt) continue;

          // Si el usuario es owner de esta unidad, sumar ownerAmount
          if (String(apt.owner) === String(userId) || String(apt.ownerSecondary) === String(userId)) {
            ownerAmount += Number(ea.owner || 0);
          }

          // Si el usuario es tenant de esta unidad, sumar tenantAmount
          if (String(apt.tenant) === String(userId) || String(apt.tenantSecondary) === String(userId)) {
            tenantAmount += Number(ea.tenant || 0);
          }
        }

        desglose[pago.id] = {
          ownerAmount: Math.round(ownerAmount * 100) / 100,
          tenantAmount: Math.round(tenantAmount * 100) / 100,
        };
      }
      
      setDesglosePayments(desglose);
    } catch (error) {
      console.error("Error cargando desglose de payments:", error);
      setDesglosePayments({});
    }
  };

  fetchPayments();
}, [selectedBuilding, selectedUnit?.id, reloadKey, effEmail, effUserid, readOnly]);

const [paymentMethod, setPaymentMethod] = useState("transferencia");
const [paidAmountInput, setPaidAmountInput] = useState("");
const [comprobante, setComprobante] = useState(null);
const [uploadingComprobante, setUploadingComprobante] = useState(false);
const [selectedPaymentOption, setSelectedPaymentOption] = useState("transferencia");
// Pago online dLocal Go: generando link / esperando confirmacion por webhook
const [dlocalLoading, setDlocalLoading] = useState(false);
const [dlocalWaiting, setDlocalWaiting] = useState(false);

// Obtener cuentas bancarias del edificio
const buildingBankAccounts = selectedBuilding?.bankAccounts || [];

const handleComprobanteChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    showToast(t("toast.fileTooLarge"), "error");
    return;
  }
  const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
  if (!allowed.includes(file.type)) {
    showToast(t("toast.fileType"), "error");
    return;
  }
  setComprobante(file);
};

const confirmarPago = async () => {
  if (readOnly) { showToast(t("toast.readOnly")); return; }
  if (!pagoSeleccionado) return;
  try {
    const user = auth.currentUser;
    if (!user) {
      showToast(t("toast.noUser"), "error");
      return;
    }

    const usersQ = query(collection(db, "users"), where("email", "==", user.email));
    const usersSnap = await getDocs(usersQ);
    const userId = usersSnap.empty
      ? user.uid
      : (usersSnap.docs[0].data().userid || usersSnap.docs[0].id);

    const nowLocal = new Date();
    const totalDebt = Number(pagoSeleccionado.remainingAmount) || Number(pagoSeleccionado.amount) || 0;
    const paidAmount = paidAmountInput ? Number(paidAmountInput) : totalDebt;

    if (!(paidAmount > 0) || !isFinite(paidAmount)) {
      showToast(t("toast.invalidAmount"), "error");
      return;
    }

    // Tope (reglas 0D): el residente informa hasta lo que debe en este pago.
    // Un sobrepago lo registra la administración al verificarlo; si lo
    // declarara el residente, el cierre lo tomaría como saldo a favor sin
    // verificación (antes FIN-017 lo dejaba pasar como crédito).
    const mensajeExcede = (pago) =>
      t("toast.excessNotAllowed", { amount: formatCurrencyUY(saldoPendienteDePago(pago)) });
    if (excedeLoAdeudado(pagoSeleccionado, paidAmount)) {
      showToast(mensajeExcede(pagoSeleccionado), "error");
      return;
    }

    // Subir comprobante si existe
    setUploadingComprobante(true);
    let comprobanteUrl = "";
    if (comprobante) {
      const ext = comprobante.name.split(".").pop();
      const storagePath = `comprobantes/${pagoSeleccionado.id}/${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, comprobante);
      comprobanteUrl = await getDownloadURL(storageRef);
    }

    const paymentRef = doc(db, "payments", pagoSeleccionado.id);
    await runTransaction(db, async (transaction) => {
      const paymentDoc = await transaction.get(paymentRef);
      if (!paymentDoc.exists()) throw new Error(t("errors.paymentNotExist"));
      const data = paymentDoc.data();
      if (data.status === "pendiente_verificacion") throw new Error(t("errors.pendingVerification"));
      if (data.paid === true) throw new Error(t("errors.alreadyRegistered"));
      if (data.publishedToResidents === false) {
        throw new Error(t("errors.notPublished"));
      }

      // Con los datos frescos: si entretanto se registró otro pago, el tope cambia.
      if (excedeLoAdeudado(data, paidAmount)) throw new Error(mensajeExcede(data));

      // Sin buildingid (contrato C9) y con montos que cuadran (utils/pagoInformado).
      const updateData = {
        ...datosPagoInformado({
          pago: data,
          monto: paidAmount,
          metodo: paymentMethod,
          paidBy: userId,
          paidByEmail: user.email,
          comprobanteUrl: comprobanteUrl || null,
        }),
        paymentDate: serverTimestamp(),
      };
      transaction.update(paymentRef, updateData);
    });

    setPayments((prev) =>
      prev.map((p) =>
        p.id === pagoSeleccionado.id
          ? {
              ...p,
              status: "pendiente_verificacion",
              paidAmount: paidAmount,
              paymentMethod: paymentMethod,
              paymentDate: nowLocal,
              paidBy: userId,
              paidByEmail: user.email,
            }
          : p
      )
    );

    setPagoSeleccionado(null);
    setPaidAmountInput("");
    setPaymentMethod("transferencia");
    setComprobante(null);
    showToast(t("toast.paymentSent"), "success");
  } catch (error) {
    console.error("Error al confirmar el pago:", error);
    showToast(error.message || t("toast.confirmError"), "error");
  } finally {
    setUploadingComprobante(false);
  }
};

// Pago online con dLocal Go: pide el link al backend y abre el checkout en otra
// pestaña. La confirmación llega async por webhook; mientras tanto escuchamos el
// doc de payments (onSnapshot, más abajo) para reflejar paid=true en vivo.
const pagarOnlineDlocal = async () => {
  if (readOnly) { showToast(t("toast.readOnly")); return; }
  if (!pagoSeleccionado || dlocalLoading) return;
  if (!selectedBuilding?.buildingid) {
    showToast(t("toast.noBuilding"), "error");
    return;
  }
  setDlocalLoading(true);
  try {
    const { redirect_url } = await initiateDlocalPayment({
      buildingId: selectedBuilding.buildingid,
      paymentId: pagoSeleccionado.id,
    });
    const win = window.open(redirect_url, "_blank", "noopener,noreferrer");
    if (!win) {
      // Popup bloqueado: redirigir en la misma pestaña como fallback.
      window.location.href = redirect_url;
      return;
    }
    setDlocalWaiting(true);
  } catch (e) {
    console.error("dLocal pago error:", e);
    showToast(e.message || t("toast.onlinePayError"), "error");
  } finally {
    setDlocalLoading(false);
  }
};

// Mientras esperamos la confirmación del pago online, escuchamos el doc de
// payments. Cuando el webhook lo marca aprobado/pagado, cerramos el modal y
// recargamos; si fue rechazado, avisamos.
useEffect(() => {
  if (!dlocalWaiting || !pagoSeleccionado?.id) return;
  const unsub = onSnapshot(doc(db, "payments", pagoSeleccionado.id), (snap) => {
    if (!snap.exists()) return;
    const d = snap.data();
    if (d.paid === true || d.status === "aprobado") {
      showToast(t("toast.paymentConfirmed"), "success");
      setDlocalWaiting(false);
      setPagoSeleccionado(null);
      setSelectedPaymentOption("transferencia");
      setReloadKey((k) => k + 1);
    } else if (d.status === "rechazado") {
      showToast(t("toast.paymentRejected"), "error");
      setDlocalWaiting(false);
    }
  });
  return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [dlocalWaiting, pagoSeleccionado?.id]);

// Vista unificada: la pantalla Gastos Comunes muestra TODOS los pagos del
// residente en el edificio actual, sin filtrar por la unidad seleccionada en
// el header. La etiqueta apt/rol va por row para que el residente sepa de
// qué unidad es cada pago. El selector del header sigue afectando otras
// pantallas; acá lo ignoramos a propósito.
//
// Si el residente es propietario en alguna unidad con inquilino, el toggle
// "Incluir pagos de mis inquilinos" agrega esos pagos a la lista (marcados
// con __isTenantPayment para mostrar etiqueta visual + fondo distinto).
const ownMarked = payments.map((p) => ({ ...p, __isTenantPayment: false }));
const tenantMarked = includeTenantPayments
  ? tenantPayments.map((p) => ({ ...p, __isTenantPayment: true }))
  : [];
const paymentsFiltrados = [...ownMarked, ...tenantMarked];
const pagosPendientes = paymentsFiltrados.filter((p) => !p.paid && p.status !== "rechazado");
const hasAnyTenantPayments = tenantPayments.length > 0;
// Solo permitimos seleccionar para el carrito los pagos que están realmente
// listos para pagar: no pagados, no en verificación, no rechazados.
const carritoCandidatos = pagosPendientes.filter((p) => p.status !== "pendiente_verificacion");
const cart = useSelectedPayments(carritoCandidatos);

// Helper: dado un payment, devuelve { apt, rol } donde apt es el apartment
// del usuario y rol es "Propietario" o "Inquilino". Usa `apartmentId` del
// payment (nuevo modelo) y cae a heurística por owner/tenant para legacy.
const resolvePaymentApt = (pago) => {
  if (!pago) return { apt: null, rol: null };
  const uidStr = String(currentUserId);
  // Match exacto por apartmentId (nuevo modelo de cierre por unidad).
  if (pago.apartmentId) {
    const apt = (userApartments || []).find((a) => a.id === pago.apartmentId);
    if (apt) {
      const isOwner =
        String(apt.owner) === uidStr || String(apt.ownerSecondary) === uidStr;
      const isTenant =
        String(apt.tenant) === uidStr || String(apt.tenantSecondary) === uidStr;
      const rol = isOwner ? t("roles.owner") : isTenant ? t("roles.tenant") : null;
      return { apt, rol };
    }
  }
  // Legacy: matchear por userid del owner/tenant.
  const apt = (userApartments || []).find(
    (a) =>
      String(a.owner) === uidStr ||
      String(a.ownerSecondary) === uidStr ||
      String(a.tenant) === uidStr ||
      String(a.tenantSecondary) === uidStr
  );
  if (!apt) return { apt: null, rol: null };
  const isOwner =
    String(apt.owner) === uidStr || String(apt.ownerSecondary) === uidStr;
  return { apt, rol: isOwner ? t("roles.owner") : t("roles.tenant") };
};

  const verDetalle = async (pago) => {
    setDetallePago(pago);
    setShowDetailModal(true);
    setLoadingDetalle(true);
    setGastosDetalle([]);
    setDesgloseGastos({});
    setAmenityChargesDetalle([]);

    try {
      // Si el pago tiene expenseIds, cargar esos gastos
      if (pago.expenseIds && pago.expenseIds.length > 0) {
        const gastosPromises = pago.expenseIds.map(async (expenseId) => {
          try {
            const expenseDoc = await getDoc(doc(db, "expenses", expenseId));
            if (expenseDoc.exists()) {
              return { id: expenseDoc.id, ...expenseDoc.data() };
            }
            return null;
          } catch (error) {
            console.error(`Error cargando expense ${expenseId}:`, error);
            return null;
          }
        });

        const gastos = await Promise.all(gastosPromises);
        const gastosFiltrados = gastos.filter(g => g !== null);
        setGastosDetalle(gastosFiltrados);

        // Cargar desglose de gastos para cada gasto
        const aptsForDetail = selectedUnit ? [selectedUnit] : userApartments;
        if (aptsForDetail.length > 0 && pago.month && pago.year && selectedBuilding?.buildingid) {
          const apartmentIds = aptsForDetail.map(apt => apt.id);
          const expensesApartmentsQuery = query(
            collection(db, "expensesapartments"),
            where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
            where("month", "==", pago.month),
            where("year", "==", pago.year)
          );
          const expensesApartmentsSnap = await getDocs(expensesApartmentsQuery);
          const expensesApartments = expensesApartmentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          // Filtrar solo los expensesapartments de las unidades del usuario
          const userExpensesApartments = expensesApartments.filter(ea =>
            apartmentIds.includes(ea.apartment)
          );

          const desglose = {};
          gastosFiltrados.forEach(gasto => {
            // Buscar todos los expensesapartments de este gasto para las unidades del usuario
            const eaDelGasto = userExpensesApartments.filter(ea => ea.expenseId === gasto.id);

            let ownerAmount = 0;
            let tenantAmount = 0;

            for (const ea of eaDelGasto) {
              const apt = aptsForDetail.find(a => a.id === ea.apartment);
              if (!apt) continue;

              // Si el usuario es owner de esta unidad, sumar ownerAmount
              if (String(apt.owner) === String(currentUserId) || String(apt.ownerSecondary) === String(currentUserId)) {
                ownerAmount += Number(ea.owner || 0);
              }

              // Si el usuario es tenant de esta unidad, sumar tenantAmount
              if (String(apt.tenant) === String(currentUserId) || String(apt.tenantSecondary) === String(currentUserId)) {
                tenantAmount += Number(ea.tenant || 0);
              }
            }

            desglose[gasto.id] = {
              ownerAmount: Math.round(ownerAmount * 100) / 100,
              tenantAmount: Math.round(tenantAmount * 100) / 100,
            };
          });

          setDesgloseGastos(desglose);
        }
      } else {
        setGastosDetalle([]);
      }

      // Cargar cargos de amenities si el pago tiene amenityChargeIds
      if (pago.amenityChargeIds && pago.amenityChargeIds.length > 0) {
        const chargePromises = pago.amenityChargeIds.map(async (chargeId) => {
          try {
            const chargeDoc = await getDoc(doc(db, "amenitycharges", chargeId));
            if (chargeDoc.exists()) {
              return { id: chargeDoc.id, ...chargeDoc.data() };
            }
            return null;
          } catch (error) {
            console.error(`Error cargando amenitycharge ${chargeId}:`, error);
            return null;
          }
        });
        const charges = await Promise.all(chargePromises);
        setAmenityChargesDetalle(charges.filter(c => c !== null));
      } else {
        setAmenityChargesDetalle([]);
      }
    } catch (error) {
      console.error("Error cargando detalle:", error);
      setGastosDetalle([]);
      setAmenityChargesDetalle([]);
    } finally {
      setLoadingDetalle(false);
    }
  };

// BUG-45 fix: Incluir pagos con paid=true aunque no tengan paymentDate
// BUG-46 fix: endDate incluye hasta el final del día seleccionado
const pagosRealizados = paymentsFiltrados.filter((p) => {
  if (!p.paid) return false;
  const fechaPago = toDate(p.paymentDate);
  // Si hay filtros de fecha pero no hay paymentDate, no mostrar en rango filtrado
  if ((startDate || endDate) && !fechaPago) return false;
  if (startDate && fechaPago && fechaPago < startDate) return false;
  if (endDate && fechaPago) {
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);
    if (fechaPago > endOfDay) return false;
  }
  return true;
});

  const tenantToggle = hasAnyTenantPayments && (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        color: appColors.ink2,
        cursor: "pointer",
        background: isMobile ? appColors.surface2 : "#f5f7fa",
        padding: "8px 12px",
        borderRadius: 8,
        marginLeft: isMobile ? 0 : "auto",
        fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
        fontWeight: 400,
      }}
    >
      <input
        type="checkbox"
        checked={includeTenantPayments}
        onChange={(e) => setIncludeTenantPayments(e.target.checked)}
      />
      {t("tenantToggle")}
    </label>
  );

  // ─── Contenido mobile: estilo app "Expensas" (screen 05) ───────────────────
  const mobileContent = (
    <AppScreen
      header={
        <AppHeader
          title={soloAlquilerEdificio ? t("header.rentTitle") : t("header.mobileTitle")}
          subtitle={soloAlquilerEdificio ? t("header.rentSubtitle") : t("header.mobileSubtitle")}
          onBack={goBack}
        />
      }
    >
      {tenantToggle && <div style={{ marginBottom: appSpacing.section }}>{tenantToggle}</div>}

      {/* Pagos pendientes — sección agrupada en card blanca con contador azul */}
      <AppCard variant="hero" padding={16} style={{ marginBottom: appSpacing.section }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: pagosPendientes.length === 0 ? 0 : 14,
          }}
        >
          <SectionLabel style={{ flex: 1 }}>{t("pending.titleMobile")}</SectionLabel>
          {pagosPendientes.length > 0 && (
            <span style={{ ...appType.stat, fontSize: 17, color: appColors.blue }}>
              {pagosPendientes.length}
            </span>
          )}
        </div>
        {pagosPendientes.length === 0 ? (
          <AppEmptyState
            icon={<FaCheckCircle />}
            title={t("pending.empty.title")}
            description={t("pending.empty.description")}
            style={{ padding: "36px 16px" }}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pagosPendientes.map((pago) => {
              const desglose = desglosePayments[pago.id];
              const isTenantPay = !!pago.__isTenantPayment;
              const { apt: pagoApt, rol: pagoRolOwn } = resolvePaymentApt(pago);
              const pagoRol = isTenantPay ? t("roles.tenant") : pagoRolOwn;
              const tenantName = isTenantPay
                ? t("roles.yourTenant")
                : null;
              const seleccionable = pago.status !== "pendiente_verificacion";
              const totalAPagar = Number(pago.remainingAmount) || Number(pago.amount) || 0;

              let diasAtraso = 0;
              if (!pago.paid && pago.maturity) {
                const matDate = new Date(pago.maturity + "T00:00:00");
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                diasAtraso = Math.floor((today - matDate) / (1000 * 60 * 60 * 24));
              }
              const atrasado = diasAtraso > 0;

              return (
                <div
                  key={pago.id}
                  style={{
                    background: appColors.cream,
                    borderRadius: appRadius.tile,
                    padding: 16,
                    borderTop: `3px solid ${atrasado ? appColors.red : appColors.blue}`,
                  }}
                >
                  {/* Encabezado del tile: descripción + monto grande a la derecha */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    {seleccionable && !readOnly && (
                      <input
                        type="checkbox"
                        aria-label={t("aria.selectPayment", { description: pago.description })}
                        checked={cart.isSelected(pago.id)}
                        onChange={() => cart.toggle(pago.id)}
                        style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0, accentColor: appColors.blue }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...appType.body, fontSize: 14.5, overflowWrap: "anywhere" }}>
                        {pago.description}
                      </div>
                      {pago.maturity && (
                        <div
                          style={{
                            ...appType.bodySm,
                            marginTop: 4,
                            color: atrasado ? appColors.red : appColors.ink2,
                          }}
                        >
                          {t("tile.dueLabel", { date: pago.maturity })}
                          {atrasado ? ` · ${t("daysLate", { count: diasAtraso })}` : ""}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 2 }}>
                        {t("tile.amount")}
                      </div>
                      <Money value={pago.amount} size={22} tone={atrasado ? "red" : undefined} />
                    </div>
                  </div>

                  {/* Footer estructurado: UNIDAD · ROL · desglose (cuando existe el dato) */}
                  {((pagoApt && pagoRol) || esPagoDeAlquiler(pago) || (desglose && (desglose.ownerAmount > 0 || desglose.tenantAmount > 0))) && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px 20px",
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: `1px solid ${appColors.line2}`,
                      }}
                    >
                      {pagoApt && pagoRol && (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("tile.unit")}</div>
                          <div style={{ ...appType.bodySm, color: appColors.ink, marginTop: 2, overflowWrap: "anywhere" }}>
                            {t("unit.numberRole", { number: pagoApt.number, role: pagoRol })}
                            {isTenantPay && tenantName ? ` · ${t("payFrom", { name: tenantName })}` : ""}
                          </div>
                        </div>
                      )}
                      {esPagoDeAlquiler(pago) && (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("tile.rent")}</div>
                          <div style={{ ...appType.bodySm, color: appColors.ink, marginTop: 2 }}>
                            {nombrePeriodo(pago.rentPeriod || { month: pago.month, year: pago.year })}
                          </div>
                        </div>
                      )}
                      {desglose && (desglose.ownerAmount > 0 || desglose.tenantAmount > 0) && (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("tile.commonExpense")}</div>
                          <div style={{ ...appType.bodySm, color: appColors.ink, marginTop: 2 }}>
                            {formatCurrencyUY(desglose.ownerAmount > 0 ? desglose.ownerAmount : desglose.tenantAmount)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Acciones */}
                  <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                    {pago.status === "pendiente_verificacion" ? (
                      <Badge tone="amber" dot>{t("status.pendingVerification")}</Badge>
                    ) : (
                      <>
                        {!readOnly && (
                          <AppButton block onClick={() => setPagoSeleccionado(pago)} icon={<FaCreditCard size={13} />}>
                            {t("buttons.payAmount", { amount: formatCurrencyUY(totalAPagar) })}
                          </AppButton>
                        )}
                        <AppButton variant="outline" block onClick={() => verDetalle(pago)} icon={<FaEye size={13} />}>
                          {t("buttons.seeDetail")}
                        </AppButton>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AppCard>

      {/* La clase habitia-datepicker no existe en el CSS y el wrapper de
          react-datepicker es inline-block: lo forzamos a full-width acá para
          que respete el flex:1 del padre y nunca desborde en pantallas angostas. */}
      <style>{`.habitia-datepicker-wrapper,.habitia-datepicker-wrapper .react-datepicker__input-container{display:block;width:100%;}`}</style>

      {/* Pagos realizados — sección agrupada en card blanca con contador azul */}
      <AppCard variant="hero" padding={16}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <SectionLabel style={{ flex: 1 }}>{t("realized.titleMobile")}</SectionLabel>
          {pagosRealizados.length > 0 && (
            <span style={{ ...appType.stat, fontSize: 17, color: appColors.green }}>
              {pagosRealizados.length}
            </span>
          )}
        </div>

        {/* Filtros de rango de fechas */}
        <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 130, flex: 1 }}>
            <label style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("filters.from")}</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="dd/MM/yyyy"
              placeholderText={t("filters.datePlaceholder")}
              customInput={<MobileDateInput />}
              wrapperClassName="habitia-datepicker-wrapper"
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 130, flex: 1 }}>
            <label style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("filters.to")}</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="dd/MM/yyyy"
              placeholderText={t("filters.datePlaceholder")}
              customInput={<MobileDateInput />}
              wrapperClassName="habitia-datepicker-wrapper"
            />
          </div>
        </div>

        {pagosRealizados.length === 0 ? (
          <AppEmptyState
            icon={<FaHistory />}
            title={t("realized.empty.title")}
            description={t("realized.empty.description")}
            style={{ padding: "36px 16px" }}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {pagosRealizados.map((pago) => {
              const fechaPago = toDate(pago.paymentDate);
              const desglose = desglosePayments[pago.id];
              const { apt: pagoApt, rol: pagoRol } = resolvePaymentApt(pago);

              return (
                <div
                  key={pago.id}
                  style={{
                    background: appColors.cream,
                    borderRadius: appRadius.tile,
                    padding: 16,
                    borderTop: `3px solid ${appColors.green}`,
                  }}
                >
                  {/* Encabezado del tile: descripción + monto + badge Confirmado */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...appType.body, fontSize: 14.5, overflowWrap: "anywhere" }}>
                        {pago.description}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <Badge tone="green" dot>{t("status.confirmed")}</Badge>
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 2 }}>
                        {t("tile.amount")}
                      </div>
                      <Money value={pago.amount} size={20} />
                    </div>
                  </div>

                  {/* Footer estructurado: FECHA · UNIDAD · desglose (cuando existe el dato) */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px 20px",
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: `1px solid ${appColors.line2}`,
                    }}
                  >
                    {fechaPago && (
                      <div style={{ minWidth: 0 }}>
                        <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("tile.date")}</div>
                        <div style={{ ...appType.bodySm, color: appColors.green, marginTop: 2 }}>
                          {t("realized.paidOn", { date: formatDateLocalized(fechaPago) })}
                        </div>
                      </div>
                    )}
                    {pagoApt && pagoRol && (
                      <div style={{ minWidth: 0 }}>
                        <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("tile.unit")}</div>
                        <div style={{ ...appType.bodySm, color: appColors.ink, marginTop: 2, overflowWrap: "anywhere" }}>
                          {t("unit.numberRole", { number: pagoApt.number, role: pagoRol })}
                        </div>
                      </div>
                    )}
                    {desglose && (desglose.ownerAmount > 0 || desglose.tenantAmount > 0) && (
                      <div style={{ minWidth: 0 }}>
                        <div style={{ ...appType.statLabel, color: appColors.ink3 }}>{t("tile.breakdownLabel")}</div>
                        <div style={{ ...appType.bodySm, color: appColors.ink, marginTop: 2, overflowWrap: "anywhere" }}>
                          {desglose.ownerAmount > 0 ? t("breakdown.ownerAmount", { amount: formatCurrencyUY(desglose.ownerAmount) }) : ""}
                          {desglose.ownerAmount > 0 && desglose.tenantAmount > 0 ? " · " : ""}
                          {desglose.tenantAmount > 0 ? t("breakdown.tenantAmount", { amount: formatCurrencyUY(desglose.tenantAmount) }) : ""}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AppCard>
    </AppScreen>
  );

  return (
    <>
    {isMobile ? mobileContent : (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <FaFileInvoiceDollar />
        </div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{soloAlquilerEdificio ? t("header.rentTitle") : t("header.title")}</h1>
          <p style={styles.subtitle}>{soloAlquilerEdificio ? t("header.rentSubtitle") : t("header.subtitle")}</p>
        </div>
        {tenantToggle}
      </div>

      {/* Pagos Pendientes */}
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <FaClock style={styles.sectionIcon} />
          <h2 style={styles.sectionTitle}>{t("pending.title")}</h2>
        </div>
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={{ ...styles.th, width: 40 }}>
                  <input
                    type="checkbox"
                    aria-label={t("aria.selectAllPending")}
                    checked={cart.count > 0 && cart.count === carritoCandidatos.length}
                    onChange={() => {
                      if (cart.count === carritoCandidatos.length) cart.clear();
                      else cart.selectAll();
                    }}
                    disabled={carritoCandidatos.length === 0 || readOnly}
                  />
                </th>
                <th style={styles.th}>{t("columns.description")}</th>
                <th style={styles.th}>{t("columns.dueDate")}</th>
                <th style={styles.th}>{t("columns.amount")}</th>
                <th style={styles.th}>{t("columns.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {pagosPendientes.map((pago) => {
                const desglose = desglosePayments[pago.id];
                const esCierreMes = pago.month && pago.year;
                const isTenantPay = !!pago.__isTenantPayment;
                const { apt: pagoApt, rol: pagoRolOwn } = resolvePaymentApt(pago);
                const pagoRol = isTenantPay ? t("roles.tenant") : pagoRolOwn;
                const tenantName = isTenantPay
                  ? t("roles.yourTenant")
                  : null;
                const seleccionable = pago.status !== "pendiente_verificacion";

                return (
                  <tr
                    key={pago.id}
                    style={{
                      ...styles.row,
                      background: isTenantPay ? "#fff7ed" : undefined,
                    }}
                  >
                    <td style={{ ...styles.td, width: 40 }}>
                      {seleccionable && !readOnly && (
                        <input
                          type="checkbox"
                          aria-label={t("aria.selectPayment", { description: pago.description })}
                          checked={cart.isSelected(pago.id)}
                          onChange={() => cart.toggle(pago.id)}
                        />
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.descriptionCell}>
                        <FaFileInvoiceDollar style={styles.rowIcon} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span>{pago.description}</span>
                          {pagoApt && pagoRol && (
                            <span style={{ fontSize: 11, color: "#6b7280" }}>
                              {t("unit.numberRoleLabeled", { number: pagoApt.number, role: pagoRol })}
                              {isTenantPay && tenantName && (
                                <span style={{ marginLeft: 6, color: "#c2410c" }}>
                                  · {t("payFrom", { name: tenantName })}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      {pago.maturity}
                      {!pago.paid && pago.maturity && (() => {
                        const matDate = new Date(pago.maturity + "T00:00:00");
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const diffMs = today - matDate;
                        const diasAtraso = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        return diasAtraso > 0 ? (
                          <span style={{ display: "inline-block", marginLeft: 6, padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 400, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
                            {t("daysLate", { count: diasAtraso })}
                          </span>
                        ) : null;
                      })()}
                    </td>
                    <td style={styles.td}>
                      {esCierreMes && desglose ? (
                        <div style={styles.desgloseContainer}>
                          <div style={styles.desgloseTotal}>{formatCurrencyUY(pago.amount)}</div>
                          <div style={styles.desgloseDetalle}>
                            {(desglose.ownerAmount > 0 || desglose.tenantAmount > 0) && (
                              <div style={styles.desgloseItem}>
                                <span style={styles.desgloseLabel}>{t("desglose.gc")}</span>
                                <span style={styles.desgloseAmount}>
                                  {formatCurrencyUY(
                                    desglose.ownerAmount > 0
                                      ? desglose.ownerAmount
                                      : desglose.tenantAmount
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span style={styles.amount}>{formatCurrencyUY(pago.amount)}</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        {pago.status === "pendiente_verificacion" ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 400, color: "#8b5cf6", background: "#f5f3ff", border: "1px solid #c4b5fd" }}>
                            <FaClock size={10} /> {t("status.pendingVerificationShort")}
                          </span>
                        ) : pago.status === "rechazado" ? (
                          <>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 12, fontSize: 11, fontWeight: 400, color: "#ef4444", background: "#fef2f2", border: "1px solid #fecaca" }}>
                              {t("status.rejected")}
                            </span>
                            {pago.rejectionReason && <span style={{ fontSize: 11, color: "#ef4444", display: "block", marginTop: 2 }}>{pago.rejectionReason}</span>}
                            {!readOnly && (
                              <button style={styles.payButton} onClick={() => setPagoSeleccionado(pago)}>
                                <FaCreditCard style={styles.buttonIcon} /> {t("status.retry")}
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <button style={styles.detailButton} onClick={() => verDetalle(pago)}>
                              <FaEye style={styles.buttonIcon} /> {t("buttons.seeDetailDesktop")}
                            </button>
                            {!readOnly && (
                              <button style={styles.payButton} onClick={() => setPagoSeleccionado(pago)}>
                                <FaCreditCard style={styles.buttonIcon} /> {t("buttons.pay")}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {pagosPendientes.length === 0 && (
                <tr>
                  <td style={styles.emptyRow} colSpan={5}>
                    <div style={styles.emptyState}>
                      <FaCheckCircle style={styles.emptyIcon} />
                      {t("pending.emptyRow")}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pagos Realizados */}
      <section style={styles.sectionSecondary}>
        <div style={styles.headerRow}>
          <div style={styles.sectionHeader}>
            <FaHistory style={styles.sectionIcon} />
            <h3 style={styles.sectionTitle}>{t("realized.title")}</h3>
          </div>
          <div style={styles.filtersRow}>
            <div style={styles.dateGroup}>
              <label style={styles.label}>
                <FaCalendarAlt style={styles.labelIcon} />
                {t("filters.from")}
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText={t("filters.datePlaceholderLong")}
                className="habitia-datepicker"
              />
            </div>
            <div style={styles.dateGroup}>
              <label style={styles.label}>
                <FaCalendarAlt style={styles.labelIcon} />
                {t("filters.to")}
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText={t("filters.datePlaceholderLong")}
                className="habitia-datepicker"
              />
            </div>
          </div>
        </div>

        <div style={styles.tableContainer}>
          <table style={styles.tableSecondary}>
            <thead>
              <tr>
                <th style={styles.thSecondary}>{t("columns.description")}</th>
                <th style={styles.thSecondary}>{t("columns.dueDate")}</th>
                <th style={styles.thSecondary}>{t("columns.amount")}</th>
                <th style={styles.thSecondary}>{t("columns.paymentDate")}</th>
                <th style={styles.thSecondary}>{t("columns.status")}</th>
              </tr>
            </thead>
            <tbody>
              {pagosRealizados.map((pago) => {
                const fechaPago = toDate(pago.paymentDate);
                const desglose = desglosePayments[pago.id];
                const esCierreMes = pago.month && pago.year;
                const { apt: pagoApt, rol: pagoRol } = resolvePaymentApt(pago);

                return (
                  <tr key={pago.id} style={styles.row}>
                    <td style={styles.tdSecondary}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span>{pago.description}</span>
                        {pagoApt && pagoRol && (
                          <span style={{ fontSize: 11, color: "#6b7280" }}>
                            {t("unit.numberRoleLabeled", { number: pagoApt.number, role: pagoRol })}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={styles.tdSecondary}>{pago.maturity}</td>
                    <td style={styles.tdSecondary}>
                      {esCierreMes && desglose ? (
                        <div style={styles.desgloseContainer}>
                          <div style={styles.desgloseTotal}>{formatCurrencyUY(pago.amount)}</div>
                          <div style={styles.desgloseDetalle}>
                            {desglose.ownerAmount > 0 && (
                              <div style={styles.desgloseItem}>
                                <span style={styles.desgloseLabel}>{t("desglose.owner")}</span>
                                <span style={styles.desgloseAmount}>{formatCurrencyUY(desglose.ownerAmount)}</span>
                              </div>
                            )}
                            {desglose.tenantAmount > 0 && (
                              <div style={styles.desgloseItem}>
                                <span style={styles.desgloseLabel}>{t("desglose.tenant")}</span>
                                <span style={styles.desgloseAmount}>{formatCurrencyUY(desglose.tenantAmount)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        `${formatCurrencyUY(pago.amount)}`
                      )}
                    </td>
                    <td style={styles.tdSecondary}>
                      {fechaPago ? formatDateLocalized(fechaPago) : "-"}
                    </td>
                    <td style={styles.tdSecondary}>
                      <span style={styles.statusPaid}>
                        <FaCheckCircle style={styles.statusIcon} />
                        {pago.confirmedByAdmin ? t("status.paidByAdmin") : t("status.paid")}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {pagosRealizados.length === 0 && (
                <tr>
                  <td style={styles.emptyRowSecondary} colSpan={5}>
                    {t("realized.emptyRow")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
    )}

      {/* ── Overlays compartidos mobile/desktop ─────────────────────────── */}
      {pagoSeleccionado && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div style={styles.iconWrapper}>
                <FaCreditCard style={styles.icon} />
              </div>
              <h3 style={styles.modalTitle}>{t("payModal.title")}</h3>
            </div>
            <p style={styles.modalText}>
              <span style={{ color: appColors.ink }}>{pagoSeleccionado.description}</span> {t("payModal.totalLabel")}{" "}
              <span style={{ color: appColors.ink }}>{formatCurrencyUY(Number(pagoSeleccionado.remainingAmount) || Number(pagoSeleccionado.amount))}</span>
            </p>
            {pagoSeleccionado.breakdown && (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14, lineHeight: 1.6 }}>
                {Object.entries(pagoSeleccionado.breakdown)
                  .filter(([, val]) => Number(val) > 0)
                  .map(([key, val]) => (
                    <div key={key}>{friendlyBreakdownName(key, selectedBuilding)}: {formatCurrencyUY(Number(val))}</div>
                  ))
                }
              </div>
            )}

            {/* Opciones de pago */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {/* Transferencia bancaria - habilitado */}
              <div
                onClick={() => setSelectedPaymentOption("transferencia")}
                style={{
                  border: selectedPaymentOption === "transferencia" ? "2px solid #0080ff" : "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "12px 14px",
                  cursor: "pointer",
                  backgroundColor: selectedPaymentOption === "transferencia" ? "#f0f7ff" : "#fff",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: "#e8f4ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaCreditCard size={16} color="#0080ff" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: "#1f2937", display: "block" }}>{t("payModal.transfer.title")}</span>
                    <span style={{ fontSize: 11, color: "#6b7280" }}>{t("payModal.transfer.desc")}</span>
                  </div>
                  <div style={{
                    width: 18, height: 18, borderRadius: 9,
                    border: selectedPaymentOption === "transferencia" ? "2px solid #0080ff" : "2px solid #d1d5db",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {selectedPaymentOption === "transferencia" && (
                      <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: "#0080ff" }} />
                    )}
                  </div>
                </div>
              </div>

              {/* Link de pago - dLocal Go (pago online) */}
              <div
                onClick={() => !dlocalWaiting && setSelectedPaymentOption("dlocal")}
                style={{
                  border: selectedPaymentOption === "dlocal" ? "2px solid #0080ff" : "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "12px 14px",
                  cursor: dlocalWaiting ? "default" : "pointer",
                  backgroundColor: selectedPaymentOption === "dlocal" ? "#f0f7ff" : "#fff",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: "#e8f4ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaExternalLinkAlt size={14} color="#0080ff" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: "#1f2937", display: "block" }}>{t("payModal.online.title")}</span>
                    <span style={{ fontSize: 11, color: "#6b7280" }}>{t("payModal.online.desc")}</span>
                  </div>
                  <div style={{
                    width: 18, height: 18, borderRadius: 9,
                    border: selectedPaymentOption === "dlocal" ? "2px solid #0080ff" : "2px solid #d1d5db",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {selectedPaymentOption === "dlocal" && (
                      <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: "#0080ff" }} />
                    )}
                  </div>
                </div>
              </div>

              {/* Débito automático - deshabilitado */}
              <div
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 10,
                  padding: "12px 14px",
                  cursor: "default",
                  backgroundColor: "#fafafa",
                  opacity: 0.55,
                  position: "relative",
                }}
                title={t("payModal.comingSoon")}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, backgroundColor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaCalendarAlt size={14} color="#9ca3af" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: "#9ca3af", display: "block" }}>{t("payModal.autoDebit.title")}</span>
                    <span style={{ fontSize: 11, color: "#c4c8cc" }}>{t("payModal.autoDebit.desc")}</span>
                  </div>
                  <span style={{ fontSize: 10, color: "#b0b5bc", backgroundColor: "#f3f4f6", padding: "2px 8px", borderRadius: 10 }}>{t("payModal.comingSoon")}</span>
                </div>
              </div>
            </div>

            {/* Contenido de transferencia bancaria */}
            {selectedPaymentOption === "transferencia" && (
              <>
                {/* Datos bancarios del edificio */}
                {buildingBankAccounts.length > 0 && (
                  <div style={{ backgroundColor: "#f0f7ff", border: "1px solid #d0e4ff", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                    <span style={{ fontSize: 11, color: "#0080ff", textTransform: "uppercase", letterSpacing: "0.3px", display: "block", marginBottom: 6 }}>
                      {t("payModal.bankData")}
                    </span>
                    {buildingBankAccounts.map((acc, idx) => (
                      <div key={acc.id || idx} style={{ fontSize: 12, color: "#1e3a5f", lineHeight: 1.7 }}>
                        <span style={{ color: "#374151" }}>{acc.bank}</span>
                        {acc.accountType && <span style={{ color: "#6b7280" }}> · {acc.accountType}</span>}
                        {acc.accountNumber && <span style={{ color: "#374151" }}> · <span style={{ color: appColors.ink }}>{acc.accountNumber}</span></span>}
                        {acc.currency && <span style={{ color: "#6b7280" }}> ({acc.currency})</span>}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 400, color: "#374151", display: "block", marginBottom: 4 }}>{t("payModal.amountLabel")}</label>
                    <input
                      type="number"
                      min="0"
                      placeholder={String(Number(pagoSeleccionado.remainingAmount) || Number(pagoSeleccionado.amount) || 0)}
                      value={paidAmountInput}
                      onChange={(e) => setPaidAmountInput(e.target.value)}
                      style={{ padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, width: "100%", boxSizing: "border-box" }}
                    />
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>{t("payModal.amountHint")}</span>
                  </div>
                </div>

                {/* Comprobante de pago */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 400, color: "#374151", display: "block", marginBottom: 4 }}>
                    {t("payModal.receiptLabel")}
                  </label>
                  {comprobante ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, background: "#f9fafb" }}>
                      <FaFileInvoiceDollar size={16} color="#0080ff" />
                      <span style={{ flex: 1, fontSize: 13, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {comprobante.name}
                      </span>
                      <button
                        onClick={() => setComprobante(null)}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}
                      >
                        <FaTimes size={12} color="#6b7280" />
                      </button>
                    </div>
                  ) : (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", border: "1px dashed #cbd5e1", borderRadius: 8, cursor: "pointer", background: "#f8fafc", color: "#64748b", fontSize: 13, transition: "all 0.2s" }}>
                      <FaCloudUploadAlt size={16} />
                      <span>{t("payModal.attachReceipt")}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={handleComprobanteChange}
                        style={{ display: "none" }}
                      />
                    </label>
                  )}
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>{t("payModal.receiptHint")}</span>
                </div>

                <div style={{ background: "rgba(217,130,0,.08)", border: "1px solid rgba(217,130,0,.25)", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: appColors.amber, marginBottom: 14 }}>
                  {t("payModal.pendingNotice")}
                </div>
              </>
            )}

            {/* Contenido pago online dLocal Go */}
            {selectedPaymentOption === "dlocal" && (
              <div style={{ marginBottom: 14 }}>
                {dlocalWaiting ? (
                  <div style={{ background: "#f0f7ff", border: "1px solid #d0e4ff", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#1e3a5f" }}>
                    {t("payModal.online.waiting")} <span style={{ color: appColors.ink }}>{t("payModal.online.waitingHighlight")}</span>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                      {t("payModal.online.waitingHint")}
                    </div>
                  </div>
                ) : (
                  <div style={{ background: "#f0f7ff", border: "1px solid #d0e4ff", borderRadius: 8, padding: "12px 14px", fontSize: 12, color: "#1e3a5f", lineHeight: 1.6 }}>
                    {t("payModal.online.introBefore")}<span style={{ color: appColors.ink }}>{formatCurrencyUY(Number(pagoSeleccionado.remainingAmount) || Number(pagoSeleccionado.amount))}</span>{t("payModal.online.introAfter")}
                  </div>
                )}
              </div>
            )}

            <div style={styles.modalButtons}>
              <button
                style={styles.cancelButton}
                onClick={() => { setPagoSeleccionado(null); setPaidAmountInput(""); setPaymentMethod("transferencia"); setComprobante(null); setSelectedPaymentOption("transferencia"); setDlocalWaiting(false); }}
              >
                {dlocalWaiting ? t("payModal.close") : t("payModal.cancel")}
              </button>
              {selectedPaymentOption === "dlocal" ? (
                <button
                  style={{ ...styles.confirmButton, opacity: (dlocalLoading || dlocalWaiting) ? 0.6 : 1, cursor: (dlocalLoading || dlocalWaiting) ? "not-allowed" : "pointer" }}
                  onClick={pagarOnlineDlocal}
                  disabled={dlocalLoading || dlocalWaiting || readOnly}
                >
                  {dlocalLoading ? t("payModal.online.generating") : dlocalWaiting ? t("payModal.online.waitingButton") : t("payModal.online.pay")}
                </button>
              ) : (
                <button
                  style={{ ...styles.confirmButton, opacity: selectedPaymentOption !== "transferencia" ? 0.4 : 1, cursor: selectedPaymentOption !== "transferencia" ? "not-allowed" : "pointer" }}
                  onClick={confirmarPago}
                  disabled={uploadingComprobante || selectedPaymentOption !== "transferencia" || readOnly}
                >
                  {uploadingComprobante ? t("payModal.transfer.uploading") : t("payModal.transfer.send")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Detalle */}
      {showDetailModal && detallePago && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, ...modalBoxWide, textAlign: "left" }}>
            <div style={{...styles.modalHeader, flexDirection: "row", justifyContent: "space-between", alignItems: "center"}}>
              <h3 style={styles.modalTitle}>{esPagoDeAlquiler(detallePago) ? t("detailModal.rentTitle") : t("detailModal.title")}</h3>
              <button
                style={styles.closeButton}
                onClick={() => {
                  setShowDetailModal(false);
                  setDetallePago(null);
                  setGastosDetalle([]);
                  setAmenityChargesDetalle([]);
                  setExplainText(null);
                }}
              >
                ✕
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailInfo}>
                <p style={styles.detailInfoParagraph}><span style={{ color: appColors.ink }}>{t("detailModal.description")}</span> {detallePago.description}</p>
                <p style={styles.detailInfoParagraph}><span style={{ color: appColors.ink }}>{t("detailModal.dueDate")}</span> {detallePago.maturity}</p>
                <p style={styles.detailInfoParagraph}><span style={{ color: appColors.ink }}>{t("detailModal.totalAmount")}</span> {formatCurrencyUY(detallePago.amount)}</p>
              </div>

              {!esPagoDeAlquiler(detallePago) && (
                <>
              {/* E1 — Explicame este gasto común */}
              <div style={{
                padding: 12,
                background: explainText ? "#e8f4ff" : "#f8f9fa",
                borderRadius: 8,
                border: explainText ? "1px solid #4da6ff" : "1px solid #e5e7eb",
                margin: "12px 0",
              }}>
                {!explainText && !explainLoading && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, color: "#374151" }}>
                      {t("explain.prompt")}
                    </span>
                    <button
                      type="button"
                      onClick={handleExplainExpense}
                      style={{
                        padding: "6px 14px",
                        fontSize: 13,
                        color: "#0080ff",
                        background: "#ffffff",
                        border: "1px solid #4da6ff",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                    >
                      {t("explain.button")}
                    </button>
                  </div>
                )}
                {explainLoading && (
                  <div style={{ fontSize: 13, color: "#6b7280" }}>{t("explain.loading")}</div>
                )}
                {explainText && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 13, color: "#0080ff", fontWeight: 400 }}>
                        {t("explain.heading")}
                      </span>
                      <button
                        type="button"
                        onClick={() => setExplainText(null)}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#6b7280",
                          cursor: "pointer",
                          fontSize: 12,
                        }}
                      >
                        {t("explain.close")}
                      </button>
                    </div>
                    <div style={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: "#1a1a1a",
                      whiteSpace: "pre-wrap",
                    }}>
                      {explainText}
                    </div>
                  </div>
                )}
              </div>
                </>
              )}

              <h4 style={styles.gastosTitle}>{esPagoDeAlquiler(detallePago) ? t("detailModal.rentConcept") : t("detailModal.includedExpenses")}</h4>

              {loadingDetalle ? (
                <p style={styles.loadingText}>{t("detailModal.loadingExpenses")}</p>
              ) : esPagoDeAlquiler(detallePago) && gastosDetalle.length === 0 ? (
                <p style={styles.emptyText}>
                  {t("detailModal.rentLine", {
                    period: nombrePeriodo(detallePago.rentPeriod || { month: detallePago.month, year: detallePago.year }),
                    amount: formatCurrencyUY(Number(detallePago.rentAmount) || Number(detallePago.breakdown?.alquiler) || 0),
                  })}
                </p>
              ) : gastosDetalle.length === 0 ? (
                <p style={styles.emptyText}>{t("detailModal.noExpenses")}</p>
              ) : (() => {
                // Preparamos las filas una sola vez para reutilizarlas en la
                // tabla (desktop) y en la lista de cards (mobile).
                const gastosRows = gastosDetalle
                  .map((gasto) => {
                    let invoiceDateStr = "N/A";
                    try {
                      if (typeof gasto.invoiceDate === 'string') {
                        invoiceDateStr = gasto.invoiceDate;
                      } else if (gasto.invoiceDate?.toDate) {
                        invoiceDateStr = formatDateLocalized(gasto.invoiceDate.toDate());
                      } else if (gasto.invoiceDate?.seconds) {
                        invoiceDateStr = formatDateLocalized(new Date(gasto.invoiceDate.seconds * 1000));
                      }
                    } catch (e) {
                      invoiceDateStr = "N/A";
                    }

                    const desgloseGasto = desgloseGastos[gasto.id];
                    const montoUsuario = desgloseGasto
                      ? (desgloseGasto.ownerAmount > 0 ? desgloseGasto.ownerAmount : desgloseGasto.tenantAmount)
                      : 0;

                    return { gasto, invoiceDateStr, montoUsuario };
                  })
                  .filter(({ montoUsuario }) => montoUsuario > 0);

                // Mobile: lista vertical de cards (sin tabla, sin scroll horizontal).
                if (isMobile) {
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                      {gastosRows.map(({ gasto, invoiceDateStr, montoUsuario }) => (
                        <div
                          key={gasto.id}
                          style={{
                            border: `1px solid ${appColors.line}`,
                            borderRadius: appRadius.cardSm,
                            padding: 14,
                            background: appColors.surface,
                          }}
                        >
                          <div style={{ ...appType.body, fontSize: 14, wordBreak: "break-word" }}>
                            {detalleCorto(gasto) || t("detailModal.noDescription")}
                          </div>
                          <div style={{ ...appType.bodySm, marginTop: 4 }}>
                            {[suppliersMap[gasto.supplierid], gasto.receiptNumber ? t("detailModal.invoiceShort", { number: gasto.receiptNumber }) : null, invoiceDateStr]
                              .filter((v) => v && v !== "N/A")
                              .join(" · ")}
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ ...appType.statLabel }}>{t("detailModal.yourPart")}</div>
                              <div style={{ ...appType.body, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>
                                {formatCurrencyUY(montoUsuario)}
                              </div>
                              <div style={{ ...appType.caption, marginTop: 2 }}>
                                {t("detailModal.totalValue", { amount: formatCurrencyUY(gasto.amount) })}
                              </div>
                            </div>
                            {gasto.bill && (
                              <a
                                href={gasto.bill}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ ...appType.bodySm, color: appColors.blue, display: "inline-flex", alignItems: "center", gap: 5, textDecoration: "none" }}
                              >
                                <FaExternalLinkAlt style={{ fontSize: "11px" }} />
                                {t("detailModal.viewInvoice")}
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }

                // Desktop: tabla, envuelta en contenedor scrollable como red de
                // seguridad para evitar que empuje el ancho del modal.
                return (
                  <div style={styles.gastosList}>
                    <table style={styles.detailTable}>
                      <thead>
                        <tr>
                          <th style={styles.detailTh}>{t("detailTable.supplier")}</th>
                          <th style={styles.detailTh}>{t("detailTable.description")}</th>
                          <th style={styles.detailTh}>{t("detailTable.invoiceNumber")}</th>
                          <th style={styles.detailTh}>{t("detailTable.invoiceDate")}</th>
                          <th style={styles.detailTh}>{t("detailTable.totalAmount")}</th>
                          <th style={styles.detailTh}>{t("detailTable.yourPart")}</th>
                          <th style={styles.detailTh}>{t("detailTable.invoice")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gastosRows.map(({ gasto, invoiceDateStr, montoUsuario }) => (
                          <tr key={gasto.id}>
                            <td style={styles.detailTd}>{suppliersMap[gasto.supplierid] || "—"}</td>
                            <td style={styles.detailTd}>{detalleCorto(gasto) || t("detailModal.noDescription")}</td>
                            <td style={styles.detailTd}>{gasto.receiptNumber || "—"}</td>
                            <td style={styles.detailTd}>{invoiceDateStr}</td>
                            <td style={styles.detailTd}>{formatCurrencyUY(gasto.amount)}</td>
                            <td style={styles.detailTd}>
                              {formatCurrencyUY(montoUsuario)}
                            </td>
                            <td style={styles.detailTd}>
                              {gasto.bill ? (
                                <a
                                  href={gasto.bill}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={styles.billLink}
                                >
                                  <FaExternalLinkAlt style={{ fontSize: "11px" }} />
                                  {t("detailTable.view")}
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Amenity Charges Section */}
              {!loadingDetalle && amenityChargesDetalle.length > 0 && (
                <>
                  <h4 style={styles.gastosTitle}>
                    <FaSwimmingPool style={{ marginRight: 8, color: "#0080ff" }} />
                    {t("amenities.title")}
                  </h4>
                  {isMobile ? (
                    // Mobile: lista vertical de cards (sin tabla, sin scroll horizontal).
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
                      {amenityChargesDetalle.map((charge) => (
                        <div
                          key={charge.id}
                          style={{
                            border: `1px solid ${appColors.line}`,
                            borderRadius: appRadius.cardSm,
                            padding: 14,
                            background: appColors.surface,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-end",
                            gap: 10,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ ...appType.body, fontSize: 14, display: "flex", alignItems: "center", gap: 6, wordBreak: "break-word" }}>
                              <FaSwimmingPool style={{ color: appColors.blue, fontSize: 13, flexShrink: 0 }} />
                              {charge.amenity || "—"}
                            </div>
                            <div style={{ ...appType.bodySm, marginTop: 4 }}>
                              {[charge.turno, charge.date].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                          <div style={{ ...appType.body, fontSize: 15, fontVariantNumeric: "tabular-nums" }}>
                            {charge.currency === "dolar" || charge.currency === "USD"
                              ? formatCurrencyUSD(charge.cost)
                              : formatCurrencyUY(charge.cost)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={styles.gastosList}>
                      <table style={styles.detailTable}>
                        <thead>
                          <tr>
                            <th style={styles.detailTh}>{t("amenitiesTable.amenity")}</th>
                            <th style={styles.detailTh}>{t("amenitiesTable.shift")}</th>
                            <th style={styles.detailTh}>{t("amenitiesTable.date")}</th>
                            <th style={styles.detailTh}>{t("amenitiesTable.cost")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {amenityChargesDetalle.map((charge) => (
                            <tr key={charge.id}>
                              <td style={styles.detailTd}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  <FaSwimmingPool style={{ color: "#0080ff", fontSize: 13 }} />
                                  {charge.amenity || "—"}
                                </div>
                              </td>
                              <td style={styles.detailTd}>{charge.turno || "—"}</td>
                              <td style={styles.detailTd}>{charge.date || "—"}</td>
                              <td style={styles.detailTd}>
                                {charge.currency === "dolar" || charge.currency === "USD"
                                  ? formatCurrencyUSD(charge.cost)
                                  : formatCurrencyUY(charge.cost)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.closeModalButton}
                onClick={() => {
                  setShowDetailModal(false);
                  setDetallePago(null);
                  setGastosDetalle([]);
                  setAmenityChargesDetalle([]);
                }}
              >
                {t("detailModal.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {cart.count > 0 && !readOnly && (
        <div
          style={{
            position: "fixed",
            bottom: isMobile ? "calc(100px + env(safe-area-inset-bottom, 0px))" : 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: appColors.surface,
            borderRadius: appRadius.pill,
            boxShadow: appShadows.hero,
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            zIndex: 50,
            fontFamily: appType.fontFamily,
          }}
        >
          <div>
            <span style={{ fontSize: 14, color: appColors.ink2 }}>
              {t("cart.paymentsCount", { count: cart.count })} ·{" "}
            </span>
            <span style={{ fontSize: 15, color: appColors.ink }}>
              {formatCurrencyUY(cart.totalAmount)}
            </span>
          </div>
          <AppButton
            variant="ghost"
            onClick={cart.clear}
            style={{ padding: "8px 14px", fontSize: 13 }}
          >
            {t("cart.clear")}
          </AppButton>
          <AppButton
            onClick={() => setShowCartCheckout(true)}
            style={{ padding: "8px 18px", fontSize: 14 }}
          >
            {t("cart.paySelected")}
          </AppButton>
        </div>
      )}

      <PaymentCheckoutModal
        open={showCartCheckout}
        onClose={() => setShowCartCheckout(false)}
        payments={cart.selectedPayments}
        buildings={buildings}
        showToast={showToast}
        onSuccess={() => {
          cart.clear();
          setReloadKey((k) => k + 1);
        }}
      />
    </>
  );
};

/* =======================
   Styles al final del archivo
   ======================= */

const styles = {
  container: {
    padding: "24px",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    background: "white",
    borderRadius: "12px",
    padding: "16px 20px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  headerIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    backgroundColor: "#0080ff",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: "20px",
    fontWeight: 400,
    margin: 0,
    color: "#0080ff",
  },
  subtitle: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "2px 0 0 0",
    fontWeight: 400,
  },
  section: {
    marginBottom: "32px",
  },
  sectionSecondary: {
    marginBottom: "24px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "16px",
  },
  sectionIcon: {
    color: "#0080ff",
    fontSize: "18px",
  },
  sectionTitle: {
    fontSize: "18px",
    margin: 0,
    color: "#1a1a1a",
    fontWeight: 400,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    gap: "16px",
    flexWrap: "wrap",
  },
  filtersRow: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
  },
  dateGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    fontSize: "13px",
  },
  label: {
    color: "#6b7280",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  labelIcon: {
    color: "#0080ff",
    fontSize: "12px",
  },
  tableContainer: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#fff",
  },
  thead: {
    backgroundColor: "#f8f9fa",
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    fontSize: "12px",
    fontWeight: 400,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #e5e7eb",
  },
  row: {
    borderBottom: "1px solid #e5e7eb",
    transition: "background-color 0.2s ease",
  },
  td: {
    padding: "16px",
    fontSize: "14px",
    color: "#4b5563",
  },
  descriptionCell: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontWeight: 400,
    color: "#1a1a1a",
  },
  rowIcon: {
    color: "#0080ff",
    fontSize: "16px",
  },
  amount: {
    fontWeight: 400,
    color: "#1a1a1a",
  },
  payButton: {
    backgroundColor: "#0080ff",
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: "13px",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  tableSecondary: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#fff",
  },
  thSecondary: {
    textAlign: "left",
    padding: "14px 16px",
    fontSize: "12px",
    fontWeight: 400,
    color: "#6b7280",
    backgroundColor: "#f8f9fa",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #e5e7eb",
  },
  tdSecondary: {
    padding: "14px 16px",
    fontSize: "14px",
    color: "#4b5563",
    borderBottom: "1px solid #e5e7eb",
  },
  statusPaid: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "5px 10px",
    backgroundColor: "#dcfce7",
    color: "#16a34a",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: 400,
  },
  statusIcon: {
    fontSize: "12px",
  },
  emptyRow: {
    textAlign: "center",
    padding: "40px 14px",
    color: "#6b7280",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
  emptyIcon: {
    fontSize: "32px",
    color: "#10b981",
    opacity: 0.5,
  },
  emptyRowSecondary: {
    textAlign: "center",
    padding: "24px 12px",
    color: "#6b7280",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
  },
  modal: {
    backgroundColor: "#fff",
    padding: "28px",
    borderRadius: "16px",
    width: "min(380px, 92vw)",
    maxHeight: "85vh",
    overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
    textAlign: "center",
  },
  modalHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
  },
  iconWrapper: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    backgroundColor: "#e8f4ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    fontSize: "24px",
    color: "#0080ff",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: 400,
    margin: 0,
    color: "#1a1a1a",
  },
  modalText: {
    fontSize: "15px",
    color: "#4b5563",
    marginBottom: "24px",
    lineHeight: "1.6",
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  cancelButton: {
    backgroundColor: "#f3f4f6",
    color: "#4b5563",
    border: "1px solid #e5e7eb",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: "14px",
    transition: "all 0.2s ease",
  },
  confirmButton: {
    backgroundColor: "#0080ff",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: "14px",
    transition: "all 0.2s ease",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  buttonIcon: {
    fontSize: "12px",
  },
  detailButton: {
    backgroundColor: "white",
    color: "#0080ff",
    border: "1px solid #0080ff",
    padding: "10px 16px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: "13px",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  closeButton: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
    padding: "4px",
    borderRadius: "4px",
    fontSize: "20px",
    fontWeight: 400,
  },
  modalBody: {
    padding: "20px",
    maxHeight: "60vh",
    overflowY: "auto",
    textAlign: "left",
  },
  detailInfo: {
    marginBottom: "20px",
    padding: "16px",
    backgroundColor: "#f8f9fa",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
  },
  detailInfoParagraph: {
    margin: "8px 0",
    fontSize: "14px",
    color: "#4b5563",
  },
  gastosTitle: {
    fontSize: "16px",
    fontWeight: 400,
    margin: "20px 0 12px 0",
    color: "#1a1a1a",
  },
  loadingText: {
    textAlign: "center",
    color: "#6b7280",
    fontStyle: "italic",
    padding: "20px",
  },
  emptyText: {
    textAlign: "center",
    color: "#6b7280",
    fontStyle: "italic",
    padding: "20px",
  },
  gastosList: {
    marginTop: "12px",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  detailTable: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#fff",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #e5e7eb",
  },
  detailTh: {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: "12px",
    fontWeight: 400,
    color: "#6b7280",
    backgroundColor: "#f8f9fa",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  detailTd: {
    padding: "12px 14px",
    fontSize: "13px",
    color: "#4b5563",
    borderTop: "1px solid #e5e7eb",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "16px 20px",
    borderTop: "1px solid #e5e7eb",
  },
  closeModalButton: {
    backgroundColor: "#6b7280",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 400,
    fontSize: "14px",
    transition: "background-color 0.2s ease",
  },
  desgloseContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  desgloseTotal: {
    fontWeight: 400,
    fontSize: "14px",
    color: "#1a1a1a",
  },
  desgloseDetalle: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    marginTop: "4px",
    fontSize: "12px",
    color: "#6b7280",
  },
  desgloseItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
  },
  desgloseLabel: {
    color: "#6b7280",
  },
  desgloseAmount: {
    fontWeight: 400,
    color: "#4b5563",
  },
  desgloseModal: {
    marginTop: "8px",
    paddingTop: "12px",
    borderTop: "1px solid #e5e7eb",
  },
  billLink: {
    color: "#0080ff",
    textDecoration: "none",
    fontWeight: 400,
    fontSize: "13px",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "6px",
    backgroundColor: "#e8f4ff",
    transition: "background-color 0.2s ease",
  },
};

export default Expensas;
